/**
 * Shared helpers for ESS Home dashboard (greeting, punches, compact calendar, holidays).
 */

import { resolveAttendanceDisplayTimeZone } from "./attendanceDayChecks";
import { workedMsFromSessions } from "./attendancePunchStorage";

export function greetingForNow(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function displayNameFromUser(user) {
  if (!user || typeof user !== "object") return "there";
  const full = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return (
    String(user.name || "").trim() ||
    full ||
    String(user.email || "").split("@")[0] ||
    "there"
  );
}

export function formatPunchTime(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return null;
  try {
    return new Date(ms).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: resolveAttendanceDisplayTimeZone(),
    });
  } catch {
    return null;
  }
}

export function formatDurationMs(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0h 00m";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/** Same session-sum rules as navbar Work Hours (via workedMsFromSessions). */
export function workedMsFromWorklog(row, nowMs = Date.now()) {
  return workedMsFromSessions(row, nowMs);
}

export function ymdLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseYmdLocal(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const parts = ymd.trim().slice(0, 10).split("-");
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Map attendance status code → compact calendar mark kind. */
export function statusToMarkKind(status) {
  const s = String(status || "")
    .trim()
    .toUpperCase();
  if (!s || s === "-" || s === "NA") return null;
  if (s === "HD" || s === "HF" || s === "HALF" || s.includes("HALF")) return "halfday";
  if (s === "PR" || s === "P" || s.startsWith("PR")) return "present";
  if (s === "WO") return "weekoff";
  if (s === "PH" || s === "HO" || s === "NH") return "holiday";
  if (s === "LOP" || s === "LP" || s === "A" || s === "AB") return "absent";
  // Leave-type codes (CL, SL, EL, ML, …) and known leave prefixes
  if (
    s === "CL" ||
    s === "SL" ||
    s === "EL" ||
    s === "ML" ||
    s === "BL" ||
    s === "PL" ||
    s === "CO" ||
    s === "CW" ||
    s === "KL" ||
    s.includes("LEAVE")
  ) {
    return "leave";
  }
  // Unknown coded status that isn't present/WO — treat as leave-ish if alphabetic leave code
  if (/^[A-Z]{1,4}$/.test(s) && !["PR", "WO", "PH", "HO", "NA"].includes(s)) return "leave";
  return null;
}

/**
 * Build 6-week calendar cells for month (0-based month).
 * Week starts Monday. attendanceByDay: { [dayNumber]: statusCode }
 */
export function buildMonthCells(year, monthIndex, attendanceByDay = {}) {
  const first = new Date(year, monthIndex, 1);
  // Monday-first: Sun(0)→6, Mon(1)→0, … Sat(6)→5
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const prevDays = new Date(year, monthIndex, 0).getDate();
  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth();
  const todayD = today.getDate();

  const cells = [];
  for (let i = 0; i < startPad; i++) {
    cells.push({
      key: `p-${i}`,
      day: prevDays - startPad + i + 1,
      mute: true,
      kind: null,
      isToday: false,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const status = attendanceByDay[d] ?? attendanceByDay[String(d)];
    cells.push({
      key: `d-${d}`,
      day: d,
      mute: false,
      kind: statusToMarkKind(status),
      isToday: year === todayY && monthIndex === todayM && d === todayD,
    });
  }
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const n = cells.length - (startPad + daysInMonth) + 1;
    cells.push({
      key: `n-${cells.length}`,
      day: n,
      mute: true,
      kind: null,
      isToday: false,
    });
    if (cells.length >= 42) break;
  }
  return cells;
}

/**
 * Enrich empty days with WO/PH from policies (same idea as EmployeeAttendance).
 */
export function enrichAttendanceWithPolicies({
  attendanceByDay,
  year,
  monthIndex,
  employee,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {},
}) {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const result = { ...attendanceByDay };
  if (!employee) return result;

  const joinDate = employee.joiningDate || employee.joinDate;
  const relievedDate = employee.relievedDate || employee.exitDate || employee.relievingDate;
  let joinNum = 0;
  let relieveNum = 99999999;
  if (joinDate) {
    const d = new Date(joinDate);
    if (!Number.isNaN(d.getTime())) {
      joinNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
  }
  if (relievedDate) {
    const d = new Date(relievedDate);
    if (!Number.isNaN(d.getTime())) {
      relieveNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
  }

  const isPolicyEffectiveOnDate = (policy, date) => {
    if (!policy?.effectiveDate) return true;
    const ed = new Date(policy.effectiveDate);
    return !Number.isNaN(ed.getTime()) && ed <= date;
  };

  const empId = employee.id || employee.employeeId || employee.empCode;
  const woMapping =
    employeeWeeklyOffMappings[empId] || employeeWeeklyOffMappings[String(empId)];
  const policyIds = woMapping
    ? Array.isArray(woMapping)
      ? woMapping
      : woMapping.policies || (woMapping.policyId != null ? [woMapping.policyId] : [])
    : [];

  const isWeeklyOff = (date) => {
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    let policy = weeklyOffPolicies.find((p) => policyIds.includes(p.id));
    if (!policy) {
      policy = weeklyOffPolicies.find(
        (p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs?.[dayName]?.enabled
      );
    }
    const wo = policy?.weeklyOffs?.[dayName];
    return !!(wo && wo.enabled);
  };

  const isHoliday = (date) => {
    const ds = ymdLocal(date);
    if ((holidaysData || []).some((h) => String(h.date || "").slice(0, 10) === ds && h.isGlobal)) {
      return true;
    }
    const empPolicyIds =
      employeeHolidayMappings[empId] || employeeHolidayMappings[String(empId)] || [];
    const policies = (holidayPolicies || []).filter((p) => empPolicyIds.includes(p.id));
    return policies.some((p) =>
      (p.holidays || []).some((h) => {
        const hd = typeof h === "object" ? h.date : h;
        return String(hd || "").slice(0, 10) === ds;
      })
    );
  };

  for (let day = 1; day <= daysInMonth; day++) {
    const currentNum = year * 10000 + (monthIndex + 1) * 100 + day;
    if (currentNum < joinNum || currentNum > relieveNum) continue;
    if (result[day] && result[day] !== "-") continue;
    const date = new Date(year, monthIndex, day);
    if (isHoliday(date)) result[day] = "PH";
    else if (isWeeklyOff(date)) result[day] = "WO";
  }
  return result;
}

/**
 * Upcoming holidays from structure holidays list (global + optional).
 */
export function pickUpcomingHolidays(holidays = [], { fromDate = new Date(), limit = 3 } = {}) {
  const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const items = (Array.isArray(holidays) ? holidays : [])
    .map((h) => {
      const raw = String(h?.date || "").slice(0, 10);
      const d = parseYmdLocal(raw);
      if (!d) return null;
      const diffDays = Math.round((d - start) / 86400000);
      if (diffDays < 0) return null;
      return {
        id: h.id ?? raw,
        name: h.name || h.holidayName || "Holiday",
        date: raw,
        dateObj: d,
        daysUntil: diffDays,
        meta:
          h.type ||
          h.holidayType ||
          (h.isOptional ? "Optional / regional" : h.isGlobal ? "National · Paid holiday" : "Holiday"),
        weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
        dayLabel: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.daysUntil - b.daysUntil);

  const seen = new Set();
  const unique = [];
  for (const item of items) {
    const key = `${item.date}|${item.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
    if (unique.length >= limit) break;
  }
  return unique;
}

export function monthTitle(year, monthIndex) {
  return new Date(year, monthIndex, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}
